/**
 * Linda Lookup Tool Definitions
 *
 * Lookup tools are executed client-side — results fed back to Claude
 * as tool_result messages for a follow-up response.
 *
 * Split from linda-tools.ts to stay within file size limits.
 */

import type {Tool} from '@/lib/litellm-client';

export const LOOKUP_TOOLS: Tool[] = [
    {
        type: 'function',
        function: {
            name: 'lookup_product',
            description:
                'Search for products by name or description and get actual results with IDs. Use this BEFORE select_product when you need to find a product by name.',
            parameters: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description:
                            'Search query (product name, description, or keywords)',
                    },
                    limit: {
                        type: 'number',
                        description:
                            'Maximum number of results to return (default: 5)',
                    },
                },
                required: ['query'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'lookup_group',
            description:
                'Search for groups by name and get actual results with IDs. Use this BEFORE select_group when you need to find a group by name.',
            parameters: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description: 'Search query (group name or keywords)',
                    },
                    limit: {
                        type: 'number',
                        description:
                            'Maximum number of results to return (default: 5)',
                    },
                },
                required: ['query'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'lookup_material',
            description:
                'Search for materials by name and get actual results with IDs. Use this BEFORE select_material when you need to find a material by name.',
            parameters: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description: 'Search query (material name or keywords)',
                    },
                    limit: {
                        type: 'number',
                        description:
                            'Maximum number of results to return (default: 5)',
                    },
                },
                required: ['query'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'ai_search_products',
            description:
                'Semantic AI search for products using vector embeddings. Use this for conceptual/style/descriptive searches like "blue pillows", "classic couch", "modern dining table", "rustic wood furniture" where you want to find products by description, style, color, or concept. Results are displayed directly in chat. Do NOT use for exact product name searches - use lookup_product for that.',
            parameters: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description:
                            'Conceptual/descriptive search query (e.g., "blue pillows", "classic couch", "mid-century modern sofa", "rustic wooden dining table")',
                    },
                    limit: {
                        type: 'number',
                        description:
                            'Maximum number of results to return (default: 6)',
                    },
                },
                required: ['query'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'generate_renders',
            description:
                'REQUIRED TOOL CALL: You MUST call this tool when the user asks you to create, generate, or make renders (silo or 360 spin) for a product. DO NOT just describe what you would do - actually call this tool. First use lookup_product to get the product ID if the user provides a product name. IMPORTANT: If the product has multiple variants (check variant count from lookup), ask the user if they want all variants or just the base product BEFORE calling this tool.',
            parameters: {
                type: 'object',
                properties: {
                    productId: {
                        type: 'string',
                        description:
                            'The product ID from lookup_product results (use "id" field, NOT "sku")',
                    },
                    renderType: {
                        type: 'string',
                        enum: ['silo', '360-spin'],
                        description:
                            'silo for static renders, 360-spin for rotating views',
                    },
                    allVariants: {
                        type: 'boolean',
                        description:
                            'Whether to render all variants (true) or just base product (false). If product has multiple variants, ask user first before setting this.',
                    },
                    message: {
                        type: 'string',
                        description:
                            'Brief confirmation message (e.g., "Starting silo renders for all 3 variants of Southpark Chair")',
                    },
                },
                required: ['productId', 'renderType', 'allVariants', 'message'],
            },
        },
    },
];
